import { useState, useCallback, useEffect } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Button,
  Form,
  Row,
  Col,
  Badge,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import ImageUpload from "components/Common/ImageUpload"
import {
  useUploadImages,
  useBulkDeleteImages,
  updateVehicleImagesCache,
} from "hooks/imageHook"
import { useToastMessage } from "hooks/useToastMessage"
import ButtonTooltip from "components/Common/ButtonTooltip"
import WarningAlert from "components/Common/WarningAlert"
import { useEditVehicleStock } from "hooks/stockHook"
import { useQueryClient } from "@tanstack/react-query"
import { generateStockTitle } from "helpers/stock_helpers"
import { compressImage, formatBytes } from "utils/imageCompression"

const VEHICLE_IMAGE_MAX_KB = 100
const VEHICLE_IMAGE_MAX_BYTES = VEHICLE_IMAGE_MAX_KB * 1024
const COMPRESSIBLE_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]

const sortPrimaryImageFirst = images => {
  const primaryImage = images.find(img => img.isPrimary)

  if (!primaryImage) {
    return images
  }

  return [
    primaryImage,
    ...images.filter(img => img.id !== primaryImage.id),
  ]
}

const sortAuctionSheetFirst = images => {
  const auctionSheet = images.find(img => img.isAuctionSheet)

  if (!auctionSheet) {
    return images
  }

  return [
    auctionSheet,
    ...images.filter(img => img.id !== auctionSheet.id),
  ]
}

const sortFeaturedImagesFirst = images => {
  const primaryImage = images.find(img => img.isPrimary)
  const auctionSheet = images.find(img => img.isAuctionSheet)
  const featuredImages = [primaryImage, auctionSheet].filter(
    (img, idx, list) =>
      img && list.findIndex(item => item?.id === img.id) === idx
  )
  const featuredIds = featuredImages.map(img => img.id)

  return [
    ...featuredImages,
    ...images.filter(img => !featuredIds.includes(img.id)),
  ]
}

const getManageableImages = stock => {
  const images = (stock?.images || []).map(img => ({
    ...img,
    isAuctionSheet: img.id === stock?.auction_sheet?.id,
  }))

  if (
    stock?.auction_sheet?.id &&
    !images.some(img => img.id === stock.auction_sheet.id)
  ) {
    images.push({
      ...stock.auction_sheet,
      isPrimary: false,
      isAuctionSheet: true,
    })
  }

  return sortFeaturedImagesFirst(images)
}

const getDisplayValue = value => {
  if (!value) return ""
  if (typeof value === "string" || typeof value === "number") return value
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (value?.name) return value.name
  if (value?.title) return value.title

  return ""
}

export default function StockImagesUpdate({
  stock = null,
  btnId = "edit-images",
  isLabel = false,
}) {
  const [modal, setModal] = useState(false)
  const [existingImages, setExistingImages] = useState([])
  const [imagesToDelete, setImagesToDelete] = useState([])
  const [imagesToUpdate, setImagesToUpdate] = useState([]) // For reordering
  const [pendingNewImages, setPendingNewImages] = useState([])
  const [auctionSheet, setAuctionSheet] = useState(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionSummary, setCompressionSummary] = useState(null)
  const toggle = () => setModal(!modal)

  const { handleResponse } = useToastMessage()
  const uploadImagesMutation = useUploadImages()
  const bulkDeleteImagesMutation = useBulkDeleteImages()
  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  // Set existing images when stock changes
  useEffect(() => {
    if (stock) {
      setExistingImages(getManageableImages(stock))
      setAuctionSheet(stock.auction_sheet || null)
    }
  }, [stock])

  const validationSchema = Yup.object({
    images: Yup.array().of(Yup.mixed()),
  })

  const queryClient = useQueryClient()

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      images: [],
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: useCallback(
      async values => {
        try {
          const formData = new FormData()

          // Handle new uploads
          if (values?.images?.length) {
            setIsCompressing(true)
            const compressedResults = []

            try {
              for (const file of values.images) {
                if (!COMPRESSIBLE_IMAGE_TYPES.includes(file.type)) {
                  compressedResults.push({
                    file,
                    originalSize: file.size,
                    compressedSize: file.size,
                    skipped: true,
                  })
                  continue
                }

                compressedResults.push(
                  await compressImage(file, {
                    maxSizeBytes: VEHICLE_IMAGE_MAX_BYTES,
                    maxWidthOrHeight: 1280,
                  })
                )
              }
            } finally {
              setIsCompressing(false)
            }

            compressedResults.forEach(result => {
              formData.append("images[]", result.file)
            })
            formData.append("max_kb", String(VEHICLE_IMAGE_MAX_KB))

            const originalSize = compressedResults.reduce(
              (total, result) => total + result.originalSize,
              0
            )
            const compressedSize = compressedResults.reduce(
              (total, result) => total + result.compressedSize,
              0
            )
            const optimizedCount = compressedResults.filter(
              result => !result.skipped && result.compressedSize < result.originalSize
            ).length

            setCompressionSummary({
              count: compressedResults.length,
              optimizedCount,
              originalSize,
              compressedSize,
            })
          }

          // Handle deletions
          if (imagesToDelete?.length) {
            imagesToDelete.forEach(id => {
              formData.append("delete_file_ids[]", id)
            })
          }

          // Process changes
          if (
            formData.has("images[]") ||
            formData.has("delete_file_ids[]") ||
            imagesToUpdate.length > 0
          ) {
            // Handle uploads/deletions first if any
            let finalImages = [...existingImages]

            if (formData.has("images[]") || formData.has("delete_file_ids[]")) {
              const uploadRes = await uploadImagesMutation.mutateAsync(formData)

              if (uploadRes.success && uploadRes.data) {
                // Filter out deleted images
                finalImages = existingImages.filter(
                  img => !imagesToDelete.includes(img.id)
                )

                // Add new images
                const uploadedImages = uploadRes.data.map((img, idx) => {
                  const pendingImage = pendingNewImages[idx]

                  return {
                    id: img.id,
                    url: img.url,
                    isPrimary: !!pendingImage?.isPrimary,
                    isAuctionSheet: !!pendingImage?.isAuctionSheet,
                  }
                })

                finalImages = [...finalImages, ...uploadedImages]
              }
            } else if (imagesToDelete.length > 0) {
              // Only deletions
              finalImages = existingImages.filter(
                img => !imagesToDelete.includes(img.id)
              )
            }

            // Apply reordering if any
            if (imagesToUpdate.length > 0) {
              const reorderedMap = new Map(
                imagesToUpdate.map((img, idx) => [img.id, idx])
              )

              finalImages.sort((a, b) => {
                const orderA = reorderedMap.get(a.id) ?? Infinity
                const orderB = reorderedMap.get(b.id) ?? Infinity
                return orderA - orderB
              })
            }

            const selectedAuctionSheet = imagesToDelete.includes(
              auctionSheet?.id
            )
              ? null
              : finalImages.find(img => img.isAuctionSheet) || auctionSheet

            finalImages = sortPrimaryImageFirst(
              finalImages.filter(img => img.id !== selectedAuctionSheet?.id)
            )

            // Update stock with final images array
            const stockRes = await editStock({
              id: stock?.id,
              images: finalImages,
              auction_sheet: selectedAuctionSheet?.id
                ? selectedAuctionSheet
                : null,
            })

            // Update cache
            const updatedImageIds = stockRes?.data?.images || []
            const auctionId = stockRes?.data?.auction_sheet?.id || null

            const query = {
              vehicleId: stock?.id,
              images: updatedImageIds,
              auctionId,
              queryClient,
            }

            updateVehicleImagesCache(query)

            handleResponse(stockRes, "Images updated successfully.", true)
            if (stockRes.success) {
              setImagesToDelete([])
              setImagesToUpdate([])
              setPendingNewImages([])
              setCompressionSummary(null)
              toggle()
              validation.resetForm()
            }
          } else {
            handleResponse(
              { success: false, message: "No changes made" },
              "Please select images to upload or delete.",
              false
            )
          }
        } catch (error) {
          console.error("Error updating images:", error)
          handleResponse(
            { success: false, message: error.message },
            "Failed to update images. Please try again.",
            false
          )
        }
      },
      [
        uploadImagesMutation,
        bulkDeleteImagesMutation,
        editStock,
        handleResponse,
        stock?.id,
        existingImages,
        imagesToDelete,
        imagesToUpdate,
        pendingNewImages,
        auctionSheet,
        queryClient,
      ]
    ),
  })

  // Handle image updates from ImageUpload component
  const handleImageUpdate = useCallback(
    ({
      allImages, // All images (existing + new)
      deletedIds, // IDs to delete
      reorderedImages, // Reordered existing images
      primaryImageId, // New primary image ID
      auctionSheetImageId, // New auction sheet image ID
    }) => {
      const nextExistingImages = reorderedImages || existingImages

      // Update existing images with reordering
      if (reorderedImages) {
        setExistingImages(reorderedImages)
        setImagesToUpdate(reorderedImages)
      }

      // Update deletion list
      if (deletedIds) {
        setImagesToDelete(prev => [...new Set([...prev, ...deletedIds])])
      }

      // Handle primary image
      if (primaryImageId) {
        const updatedImages = sortPrimaryImageFirst(
          nextExistingImages.map(img => ({
            ...img,
            isPrimary: img.id === primaryImageId,
            isAuctionSheet:
              img.id === primaryImageId ? false : img.isAuctionSheet,
          }))
        )
        setExistingImages(updatedImages)
        setImagesToUpdate(updatedImages)
      }

      // Handle auction sheet image
      if (auctionSheetImageId) {
        const updatedImages = sortAuctionSheetFirst(
          nextExistingImages.map(img => ({
            ...img,
            isPrimary: img.id === auctionSheetImageId ? false : img.isPrimary,
            isAuctionSheet: img.id === auctionSheetImageId,
          }))
        )
        const updatedAuctionSheet = updatedImages.find(
          img => img.id === auctionSheetImageId
        )

        setAuctionSheet(updatedAuctionSheet || null)
        setExistingImages(updatedImages)
        setImagesToUpdate(updatedImages)
      }

      // Update formik values with new files
      if (allImages) {
        const nextPendingNewImages = allImages.filter(img => img.file)
        const newFiles = nextPendingNewImages.map(img => img.file)
        setPendingNewImages(nextPendingNewImages)
        validation.setFieldValue("images", newFiles)
      }
    },
    [existingImages, validation]
  )

  // Reset form when modal opens
  useEffect(() => {
    if (modal) {
      validation.setValues({ images: [] })
      setExistingImages(getManageableImages(stock))
      setImagesToDelete([])
      setImagesToUpdate([])
      setPendingNewImages([])
      setAuctionSheet(stock?.auction_sheet || null)
      setCompressionSummary(null)
    }
  }, [modal, stock])

  const isProcessing =
    uploadImagesMutation.isPending ||
    bulkDeleteImagesMutation.isPending ||
    stockEditLoading ||
    isCompressing
  const pendingUploadCount = validation.values.images.length
  const currentImageCount = existingImages.length
  const hasChanges =
    pendingUploadCount > 0 ||
    imagesToDelete.length > 0 ||
    imagesToUpdate.length > 0
  const stockTitle = generateStockTitle(stock) || "Vehicle stock"
  const stockSubtitle = [
    stock?.year?.name,
    stock?.package?.name ? `${stock.package.name} Package` : null,
    stock?.exterior_color?.name,
  ]
    .filter(Boolean)
    .join(" - ")
  const stockDetails = [
    {
      label: "Stock ID",
      value: stock?.stock_id,
      icon: "bx-barcode",
    },
    {
      label: "Chassis",
      value: stock?.full_chassis,
      icon: "bx-hash",
    },
    {
      label: "Showroom",
      value: [stock?.showroom?.name, stock?.showroom?.short_name]
        .filter(Boolean)
        .join(" - "),
      icon: "bx-map",
    },
    {
      label: "Status",
      value: getDisplayValue(stock?.status),
      icon: "bx-check-shield",
    },
  ].filter(item => item.value)
  const stockPreviewImage =
    existingImages.find(img => img.isPrimary)?.url || existingImages[0]?.url
  const hasPendingAuctionSheet = pendingNewImages.some(
    img => img.isAuctionSheet
  )

  const imageStats = [
    {
      label: "Current",
      value: currentImageCount,
      icon: "bx-images",
      className: "stock-images-update-modal__stat--current",
    },
    {
      label: "Queued",
      value: pendingUploadCount,
      icon: "bx-cloud-upload",
      className: "stock-images-update-modal__stat--queued",
    },
    {
      label: "Removing",
      value: imagesToDelete.length,
      icon: "bx-trash",
      className: "stock-images-update-modal__stat--removing",
    },
    {
      label: "Auction",
      value:
        hasPendingAuctionSheet ||
        (auctionSheet?.id && !imagesToDelete.includes(auctionSheet.id))
          ? "Set"
          : "None",
      icon: "bx-file",
      className: "stock-images-update-modal__stat--auction",
    },
  ]
  const imagePurposes = [
    {
      title: "Website gallery",
      text: "Photos buyers review on the vehicle page.",
      icon: "bx-store",
    },
    {
      title: "Main listing photo",
      text: "Primary image used in listings and previews.",
      icon: "bx-star",
    },
    {
      title: "Auction sheet",
      text: "Reference image kept separate from gallery photos.",
      icon: "bx-file",
    },
    {
      title: "Compressed upload",
      text: `Browser and server cap images at ${VEHICLE_IMAGE_MAX_KB}KB.`,
      icon: "bx-compress",
    },
  ]

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId={btnId}
        btnName={isLabel ? " Edit Images" : "Edit Images"}
        is_label={isLabel}
      />

      <Modal
        isOpen={modal}
        toggle={toggle}
        centered
        size="xl"
        className="stock-images-update-modal"
      >
        <ModalHeader toggle={toggle} className="border-0 px-4 pt-4 pb-0">
          <div className="stock-images-update-modal__title-wrap">
            <span className="stock-images-update-modal__eyebrow">
              Stock media
            </span>
            <h4 className="mb-1">Vehicle images</h4>
            <p className="text-muted mb-0 fw-normal">
              Confirm the stock, then tune the gallery and auction reference.
            </p>
          </div>
        </ModalHeader>
        <ModalBody className="px-4 pb-0">
          <div className="stock-images-update-modal__stock">
            <div className="stock-images-update-modal__stock-preview">
              {stockPreviewImage ? (
                <img src={stockPreviewImage} alt={stockTitle} />
              ) : (
                <i className="bx bx-car"></i>
              )}
            </div>
            <div className="stock-images-update-modal__stock-main">
              <div className="stock-images-update-modal__stock-heading">
                <div className="min-width-0">
                  <h5 className="mb-1 text-truncate">{stockTitle}</h5>
                  {stockSubtitle ? (
                    <p className="text-muted mb-0 text-truncate">
                      {stockSubtitle}
                    </p>
                  ) : null}
                </div>
                <div className="stock-images-update-modal__badges">
                  {stock?.is_published_on_website ? (
                    <Badge className="bg-success bg-opacity-10 text-success border-0">
                      <i className="bx bx-globe me-1"></i>
                      Live
                    </Badge>
                  ) : null}
                  <Badge
                    className={`border-0 ${
                      stock?.is_published
                        ? "bg-primary bg-opacity-10 text-primary"
                        : "bg-warning bg-opacity-10 text-warning"
                    }`}
                  >
                    {stock?.is_published ? "Published" : "Draft"}
                  </Badge>
                </div>
              </div>

              <div className="stock-images-update-modal__stock-details">
                {stockDetails.map(item => (
                  <div
                    className="stock-images-update-modal__stock-detail"
                    key={item.label}
                  >
                    <i className={`bx ${item.icon}`}></i>
                    <div>
                      <span>{item.label}</span>
                      <strong title={item.value}>{item.value}</strong>
                    </div>
                  </div>
                ))}
              </div>

              <div className="stock-images-update-modal__purpose">
                {imagePurposes.map(item => (
                  <div
                    className="stock-images-update-modal__purpose-item"
                    key={item.title}
                  >
                    <i className={`bx ${item.icon}`}></i>
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="stock-images-update-modal__summary">
            <Row className="g-3">
              {imageStats.map(stat => (
                <Col xs={6} lg={3} key={stat.label}>
                  <div
                    className={`stock-images-update-modal__stat ${stat.className}`}
                  >
                    <span className="stock-images-update-modal__stat-icon">
                      <i className={`bx ${stat.icon}`}></i>
                    </span>
                    <span className="stock-images-update-modal__stat-label">
                      {stat.label}
                    </span>
                    <strong>{stat.value}</strong>
                  </div>
                </Col>
              ))}
            </Row>
          </div>

          <WarningAlert message="Use clear, high-quality vehicle photos. Primary and auction sheet selections are applied when changes are saved." />

          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            {(isCompressing || compressionSummary) && (
              <div className="stock-images-update-modal__compression">
                <i
                  className={`bx ${
                    isCompressing ? "bx-loader-alt bx-spin" : "bx-check-circle"
                  }`}
                ></i>
                <span>
                  {isCompressing
                    ? `Optimizing selected images to ${VEHICLE_IMAGE_MAX_KB}KB each...`
                    : `${compressionSummary.optimizedCount} of ${compressionSummary.count} image(s) optimized - ${formatBytes(
                        compressionSummary.originalSize
                      )} to ${formatBytes(compressionSummary.compressedSize)}`}
                </span>
              </div>
            )}

            <div className="stock-images-update-modal__uploader">
              <ImageUpload
                existingImages={existingImages}
                onImagesChange={handleImageUpdate}
                maxFiles={30}
                maxSizeMB={5}
                acceptedFileTypes="image/*"
                multiple={true}
                title="Vehicle gallery"
                subtitle={`JPG, PNG, or WebP up to 5MB each. Uploads are compressed to ${VEHICLE_IMAGE_MAX_KB}KB before save.`}
                showAuctionSheetAction={true}
                disabled={isProcessing}
              />
            </div>

            <div className="stock-images-update-modal__actions">
              <Button
                type="button"
                color="light"
                onClick={toggle}
                disabled={isProcessing}
                className="px-4"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="success"
                disabled={isProcessing || !hasChanges}
                className="px-4"
              >
                {isProcessing ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {isCompressing ? "Optimizing..." : "Saving..."}
                  </>
                ) : (
                  <>
                    <i className="bx bx-save me-2"></i>
                    Save changes
                  </>
                )}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      <style>{`
        .stock-images-update-modal .modal-content {
          border: 0;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.12);
        }

        .stock-images-update-modal .modal-header {
          background: #ffffff;
          border-bottom: 1px solid #edf1f7 !important;
        }

        .stock-images-update-modal .btn-close {
          background-color: #f1f5f9;
          border-radius: 50%;
          margin-top: 0.25rem;
          opacity: 0.8;
          padding: 0.8rem;
        }

        .stock-images-update-modal .btn-close:hover {
          background-color: #e2e8f0;
          opacity: 1;
        }

        .stock-images-update-modal__title-wrap {
          max-width: 620px;
        }

        .stock-images-update-modal__title-wrap h4 {
          font-size: 1.05rem;
          font-weight: 650;
        }

        .stock-images-update-modal__title-wrap p {
          font-size: 0.84rem;
        }

        .stock-images-update-modal__eyebrow {
          color: #556ee6;
          display: inline-block;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0;
          margin-bottom: 0.25rem;
          text-transform: uppercase;
        }

        .stock-images-update-modal__stock {
          align-items: center;
          background: #fbfcfe;
          border: 1px solid #edf1f7;
          border-radius: 12px;
          display: flex;
          gap: 0.85rem;
          margin: 1rem 0;
          padding: 0.75rem;
        }

        .stock-images-update-modal__stock-preview {
          align-items: center;
          background: #f1f5f9;
          border-radius: 10px;
          color: #64748b;
          display: flex;
          flex: 0 0 74px;
          font-size: 1.5rem;
          height: 74px;
          justify-content: center;
          overflow: hidden;
        }

        .stock-images-update-modal__stock-preview img {
          height: 100%;
          object-fit: cover;
          width: 100%;
        }

        .stock-images-update-modal__stock-main {
          flex: 1;
          min-width: 0;
        }

        .stock-images-update-modal__stock-main .min-width-0 {
          min-width: 0;
        }

        .stock-images-update-modal__stock-heading {
          align-items: flex-start;
          display: flex;
          gap: 0.75rem;
          justify-content: space-between;
          margin-bottom: 0.65rem;
        }

        .stock-images-update-modal__stock-heading h5 {
          font-size: 0.96rem;
          font-weight: 650;
          line-height: 1.25;
        }

        .stock-images-update-modal__stock-heading p {
          font-size: 0.78rem;
        }

        .stock-images-update-modal__badges {
          align-items: center;
          display: flex;
          flex: 0 0 auto;
          flex-wrap: wrap;
          gap: 0.35rem;
          justify-content: flex-end;
        }

        .stock-images-update-modal__badges .badge {
          border-radius: 999px;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.3rem 0.5rem;
        }

        .stock-images-update-modal__stock-details {
          display: grid;
          gap: 0.45rem;
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .stock-images-update-modal__stock-detail {
          align-items: center;
          background: #ffffff;
          border: 1px solid #edf1f7;
          border-radius: 8px;
          display: flex;
          gap: 0.45rem;
          min-height: 44px;
          padding: 0.5rem;
        }

        .stock-images-update-modal__stock-detail i {
          align-items: center;
          background: #f4f7ff;
          border-radius: 8px;
          color: #556ee6;
          display: inline-flex;
          flex: 0 0 28px;
          font-size: 1rem;
          height: 28px;
          justify-content: center;
        }

        .stock-images-update-modal__stock-detail span {
          color: #64748b;
          display: block;
          font-size: 0.66rem;
          font-weight: 600;
          line-height: 1.1;
        }

        .stock-images-update-modal__stock-detail strong {
          color: #0f172a;
          display: block;
          font-size: 0.78rem;
          line-height: 1.25;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .stock-images-update-modal__purpose {
          display: flex;
          flex-wrap: wrap;
          gap: 0.45rem;
          margin-top: 0.65rem;
        }

        .stock-images-update-modal__purpose-item {
          align-items: center;
          background: #ffffff;
          border: 1px solid #edf1f7;
          border-radius: 999px;
          display: flex;
          gap: 0.4rem;
          min-height: 30px;
          padding: 0.35rem 0.65rem;
        }

        .stock-images-update-modal__purpose-item i {
          color: #34a853;
          flex: 0 0 auto;
          font-size: 0.95rem;
          line-height: 1.1;
        }

        .stock-images-update-modal__purpose-item strong {
          color: #0f172a;
          display: inline;
          font-size: 0.74rem;
          line-height: 1.2;
        }

        .stock-images-update-modal__purpose-item span {
          color: #64748b;
          display: inline;
          font-size: 0.72rem;
          line-height: 1.35;
          margin-left: 0.25rem;
        }

        .stock-images-update-modal__summary {
          background: transparent;
          border: 0;
          margin: 0 0 0.8rem;
          padding: 0;
        }

        .stock-images-update-modal .alert {
          border-radius: 10px;
          font-size: 0.78rem;
          margin-bottom: 0.8rem;
          padding: 0.55rem 0.75rem;
        }

        .stock-images-update-modal__compression {
          align-items: center;
          background: #f1fbf6;
          border: 1px solid #d8f1e3;
          border-radius: 10px;
          color: #237a3b;
          display: flex;
          font-size: 0.78rem;
          gap: 0.45rem;
          margin-bottom: 0.8rem;
          padding: 0.5rem 0.7rem;
        }

        .stock-images-update-modal__compression i {
          font-size: 1rem;
        }

        .stock-images-update-modal__stat {
          align-items: center;
          background: #ffffff;
          border: 1px solid #edf1f7;
          border-radius: 9px;
          display: grid;
          gap: 0.05rem 0.5rem;
          grid-template-columns: 30px 1fr;
          min-height: 52px;
          padding: 0.5rem 0.6rem;
        }

        .stock-images-update-modal__stat-icon {
          align-items: center;
          border-radius: 8px;
          display: inline-flex;
          font-size: 1rem;
          grid-row: span 2;
          height: 30px;
          justify-content: center;
          width: 30px;
        }

        .stock-images-update-modal__stat-label {
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 600;
          line-height: 1.1;
        }

        .stock-images-update-modal__stat strong {
          color: #0f172a;
          font-size: 0.88rem;
          line-height: 1.1;
        }

        .stock-images-update-modal__stat--current .stock-images-update-modal__stat-icon {
          background: #e9f2ff;
          color: #3577f1;
        }

        .stock-images-update-modal__stat--queued .stock-images-update-modal__stat-icon {
          background: #e8f7ee;
          color: #34a853;
        }

        .stock-images-update-modal__stat--removing .stock-images-update-modal__stat-icon {
          background: #fdecec;
          color: #f46a6a;
        }

        .stock-images-update-modal__stat--auction .stock-images-update-modal__stat-icon {
          background: #e7f8fb;
          color: #50a5f1;
        }

        .stock-images-update-modal__uploader {
          background: #ffffff;
          border: 1px solid #edf1f7;
          border-radius: 12px;
          padding: 0.9rem;
        }

        .stock-images-update-modal .image-upload .card-title {
          font-size: 0.9rem;
          font-weight: 650;
          margin-bottom: 0.2rem;
        }

        .stock-images-update-modal .image-upload > p {
          font-size: 0.78rem;
          margin-bottom: 0.7rem !important;
        }

        .stock-images-update-modal .dropzone {
          background: #fbfdff;
          border: 1px dashed #c3cce6;
          border-radius: 12px;
          margin-bottom: 0;
          min-height: 132px;
          transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
        }

        .stock-images-update-modal .dropzone .dz-message {
          margin: 1.2rem 0;
        }

        .stock-images-update-modal .dropzone .display-4 {
          font-size: 2.25rem;
        }

        .stock-images-update-modal .dropzone h5 {
          font-size: 0.95rem;
          font-weight: 650;
        }

        .stock-images-update-modal .dropzone .btn {
          border-radius: 8px;
          font-size: 0.78rem;
          padding: 0.4rem 0.75rem;
        }

        .stock-images-update-modal .dropzone.dragging,
        .stock-images-update-modal .dropzone:hover {
          background: #f3f7ff;
          border-color: #556ee6;
          box-shadow: inset 0 0 0 1px rgba(85, 110, 230, 0.12);
        }

        .stock-images-update-modal .selected-files .card {
          border: 1px solid #edf1f7;
          border-radius: 10px;
          box-shadow: none;
          transition: transform 180ms cubic-bezier(0.2, 0, 0, 1), border-color 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
        }

        .stock-images-update-modal .selected-files .card-body {
          padding: 0.55rem 0.75rem;
        }

        .stock-images-update-modal .selected-files img {
          border-radius: 8px !important;
          height: 56px !important;
          width: 56px !important;
        }

        .stock-images-update-modal .selected-files .card:hover {
          border-color: #cfd8ea;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
        }

        .stock-images-update-modal__actions {
          align-items: center;
          background: rgba(255, 255, 255, 0.96);
          border-top: 1px solid #e8edf5;
          display: flex;
          gap: 0.75rem;
          justify-content: flex-end;
          margin: 1rem -1.5rem 0;
          padding: 0.75rem 1.5rem;
          position: sticky;
          bottom: 0;
          z-index: 2;
        }

        .stock-images-update-modal__actions .btn {
          border-radius: 8px;
        }

        @media (max-width: 991.98px) {
          .stock-images-update-modal__stock-details {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 575.98px) {
          .stock-images-update-modal .modal-content {
            border-radius: 14px;
          }

          .stock-images-update-modal__stock {
            flex-direction: column;
          }

          .stock-images-update-modal__stock-preview {
            flex-basis: auto;
            height: 150px;
          }

          .stock-images-update-modal__purpose {
            align-items: stretch;
            flex-direction: column;
          }

          .stock-images-update-modal__stock-heading {
            flex-direction: column;
          }

          .stock-images-update-modal__badges {
            justify-content: flex-start;
          }

          .stock-images-update-modal__stock-details {
            grid-template-columns: 1fr;
          }

          .stock-images-update-modal__uploader {
            padding: 1rem;
          }

          .stock-images-update-modal__actions {
            flex-direction: column-reverse;
          }

          .stock-images-update-modal__actions .btn {
            width: 100%;
          }
        }
      `}</style>
    </>
  )
}
